import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Pagination } from "./ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { 
  Users, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Phone, 
  Mail, 
  Shield, 
  Eye, 
  Building2, 
  Loader2,
  UserCheck,
  UserX
} from "lucide-react";
import { toast } from "sonner";
import { userApiService, User } from "../services";
import { useBranch } from "../contexts/BranchContext";
import { API_CONFIG } from "../config/api";
import { PermissionGate } from "./ui/PermissionGate";

export function UserListingPage() {
  const navigate = useNavigate();
  const { selectedBranch, setSelectedBranch } = useBranch();
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [branchLoading, setBranchLoading] = useState(false);

  // Debug branch context
  // console.log('🔧 UserListingPage render - selectedBranch:', selectedBranch);
  
  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  // User management route (from backend permissions)
  const USER_ROUTE = '/users';

  // Load users from API
  const loadUsers = async () => {
    try {
      setLoading(true);
      if (selectedBranch) {
        setBranchLoading(true);
      }
      const branchId = selectedBranch || undefined;
      // console.log('🔍 loadUsers called with:', { currentPage, pageSize, selectedBranch, branchId });
      const response = await userApiService.getUsers(currentPage, pageSize, branchId);
      
      if (response.success) {
        setUsers(response.data);
        setTotalCount(response.pagination?.totalCount || 0);
        setTotalPages(response.pagination?.totalPages || 1);
      } else {
        toast.error(response.message || 'Failed to load users');
      }
    } catch (error) {
      // console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
      setBranchLoading(false);
    }
  };

  // Search users
  const searchUsers = async () => {
    if (!searchTerm.trim()) {
      loadUsers();
      return;
    }

    try {
      setLoading(true);
      const branchId = selectedBranch || undefined;
      // console.log('🔍 searchUsers called with:', { searchTerm, selectedBranch, branchId });
      
      // Reset to first page when searching
      setCurrentPage(1);
      
      const response = await userApiService.searchUsers(searchTerm, 1, 10, branchId);
      // console.log('🔍 Search API response:', response);
      
      if (response.success) {
        setUsers(response.data);
        // Search API doesn't support pagination, so set total count to actual results
        setTotalCount(response.data.length);
        setTotalPages(1);
        // console.log('🔍 Search results:', { users: response.data, totalCount: response.data.length, totalPages: 1 });
      } else {
        // console.log('🔍 Search failed:', response.message);
        toast.error(response.message || 'Failed to search users');
      }
    } catch (error) {
      // console.error('🔍 Error searching users:', error);
      toast.error('Failed to search users');
    } finally {
      setLoading(false);
    }
  };

  // Delete user
  const handleDeleteUser = async (userId: number) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await userApiService.deleteUser(userId.toString());
        toast.success('User deleted successfully');
        loadUsers(); // Refresh the list
      } catch (error) {
        // console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Initial load on component mount - only when branch is available
  useEffect(() => {
    if (selectedBranch !== null) {
      loadUsers();
    }
  }, [selectedBranch]); // Run when selectedBranch changes from null to a value

  // Handle search with debounce and branch changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.trim()) {
        searchUsers();
      } else {
        loadUsers();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, selectedBranch]); // Removed currentPage dependency since search resets it

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
        <UserCheck className="w-3 h-3 mr-1" />
        Active
      </Badge>
    ) : (
      <Badge variant="secondary" className="bg-red-100 text-red-800 hover:bg-red-100">
        <UserX className="w-3 h-3 mr-1" />
        Inactive
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-1">
            Manage system users, roles, and permissions
            {selectedBranch && (
              <span className="ml-2 text-primary-600 font-medium">
                • Filtered by Branch {selectedBranch}
              </span>
            )}
          </p>
        </div>
        <PermissionGate route={USER_ROUTE} action="create">
          <Button 
            onClick={() => navigate('/users/new')}
            className="bg-primary-600 hover:bg-primary-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </PermissionGate>
      </div>

      {/* Branch Loading State */}
      {selectedBranch === null && (
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600 mr-3" />
            <span className="text-gray-600">Loading branch information...</span>
          </CardContent>
        </Card>
      )}

      {/* Statistics Cards */}
      {selectedBranch !== null && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {selectedBranch ? 'Branch Users' : 'Total Users'}
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalCount}</div>
              <p className="text-xs text-muted-foreground">
                {users.filter(u => u.isActive).length} active users
                {selectedBranch && ` in branch ${selectedBranch}`}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Users</CardTitle>
              <UserCheck className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {users.filter(u => u.isActive).length}
              </div>
              <p className="text-xs text-muted-foreground">
                {selectedBranch ? `Active in branch ${selectedBranch}` : 'Currently active'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Inactive Users</CardTitle>
              <UserX className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {users.filter(u => !u.isActive).length}
              </div>
              <p className="text-xs text-muted-foreground">
                {selectedBranch ? `Inactive in branch ${selectedBranch}` : 'Currently inactive'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">New This Month</CardTitle>
              <Users className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {users.filter(u => {
                  const joinDate = new Date(u.joinDate);
                  const now = new Date();
                  return joinDate.getMonth() === now.getMonth() && 
                         joinDate.getFullYear() === now.getFullYear();
                }).length}
              </div>
              <p className="text-xs text-muted-foreground">
                Joined this month
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      {selectedBranch !== null && (
        <Card>
          <CardHeader>
            <CardTitle>Search Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search by name, email, username, employee ID, or department..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {selectedBranch && (
                  <div className="flex items-center space-x-2 px-3 py-2 bg-primary-50 border border-primary-200 rounded-md">
                    <Building2 className="w-4 h-4 text-primary-600" />
                    <span className="text-sm text-primary-700">
                      Branch: {selectedBranch}
                      {branchLoading && <Loader2 className="w-3 h-3 ml-1 animate-spin" />}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedBranch(null)}
                      className="h-6 w-6 p-0 hover:bg-primary-100"
                    >
                      <span className="sr-only">Clear branch filter</span>
                      ×
                    </Button>
                  </div>
                )}
              </div>
              <Button 
                onClick={searchUsers}
                disabled={loading}
                className="bg-primary-600 hover:bg-primary-700"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Search
              </Button>
            </div>
          </CardContent>

        </Card>
      )}

      {/* Users Table */}
      {selectedBranch !== null && (
        <Card>
          <CardHeader>
            <CardTitle>
              Users
              {selectedBranch && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  (Branch {selectedBranch})
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                <span className="ml-2 text-gray-600">Loading users...</span>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p>No users found</p>
                {searchTerm && <p className="text-sm">Try adjusting your search criteria</p>}
                {selectedBranch && !searchTerm && (
                  <p className="text-sm">No users found in the selected branch</p>
                )}
              </div>
            ) : (
              <>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Employee ID</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Join Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                                <span className="text-primary-600 font-semibold text-sm">
                                  {(user.firstName?.charAt(0) || '') + (user.lastName?.charAt(0) || '') || 'U'}
                                </span>
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">
                                  {user.firstName || ''} {user.lastName || ''}
                                </div>
                                <div className="text-sm text-gray-500">@{user.username || 'N/A'}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm font-medium text-gray-900">
                              {user.employeeId}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm text-gray-600">
                              {user.department || 'N/A'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center text-sm">
                                <Mail className="w-4 h-4 mr-2 text-gray-400" />
                                {user.email}
                              </div>
                              {user.phoneNumber && (
                                <div className="flex items-center text-sm">
                                  <Phone className="w-4 h-4 mr-2 text-gray-400" />
                                  {user.phoneNumber}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(user.isActive)}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm text-gray-500">
                              {formatDate(user.joinDate)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <PermissionGate route={USER_ROUTE} action="view">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => navigate(`/users/${user.id}`)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </PermissionGate>
                              <PermissionGate route={USER_ROUTE} action="edit">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => navigate(`/users/${user.id}/edit`)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </PermissionGate>
                              <PermissionGate route={USER_ROUTE} action="delete">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteUser(user.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </PermissionGate>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 flex justify-center">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
